import type { Order, OrderHistoryEntry, OrderStatus, PaginatedOrders } from '@shop-monorepo/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { message } from 'antd';

import { apiClient } from '../services/api';

const ORDER_QUERY_KEY = 'orders';
const ORDER_HISTORY_QUERY_KEY = 'order-history';

interface OrdersQueryParams {
  page?: number;
  limit?: number;
  filters?: {
    status?: string;
    isPaid?: boolean;
    isCashed?: boolean;
    deliveryProvider?: string;
  };
  sorter?: { field?: string; direction?: 'asc' | 'desc' };
}

export const useOrders = (params: OrdersQueryParams) => {
  return useQuery<PaginatedOrders, Error>({
    queryKey: [ORDER_QUERY_KEY, params],
    queryFn: async () => {
      const { page = 1, limit = 10, filters, sorter } = params;
      return apiClient.orders.all(page, limit, filters, sorter);
    },
    placeholderData: (previousData) => previousData,
  });
};

export const useOrder = (id: number) => {
  return useQuery<Order, Error>({
    queryKey: [ORDER_QUERY_KEY, id],
    queryFn: () => apiClient.orders.getById(id),
    enabled: !!id,
  });
};

export const useUpdateOrderStatus = () => {
  return useMutation<Order, Error, { id: number; status: OrderStatus }>({
    mutationFn: ({ id, status }) => apiClient.orders.updateStatus(id, status),
    meta: { invalidates: [[ORDER_QUERY_KEY], [ORDER_HISTORY_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Статус замовлення оновлено!');
    },
    onError: (error) => {
      message.error(`Не вдалося оновити статус: ${error.message}`);
    },
  });
};

export const useUpdateOrderPayment = () => {
  return useMutation<Order, Error, { id: number; isPaid: boolean }>({
    mutationFn: ({ id, isPaid }) => apiClient.orders.updatePayment(id, isPaid),
    meta: { invalidates: [[ORDER_QUERY_KEY], [ORDER_HISTORY_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Статус оплати оновлено!');
    },
    onError: (error) => {
      message.error(`Не вдалося оновити оплату: ${error.message}`);
    },
  });
};

export const useUpdateOrderCash = () => {
  return useMutation<Order, Error, { id: number; isCashed: boolean }>({
    mutationFn: ({ id, isCashed }) => apiClient.orders.updateCash(id, isCashed),
    meta: { invalidates: [[ORDER_QUERY_KEY], [ORDER_HISTORY_QUERY_KEY]] },
    onSuccess: () => {
      message.success('Статус каси оновлено!');
    },
    onError: (error) => {
      message.error(`Не вдалося оновити статус каси: ${error.message}`);
    },
  });
};

export const useOrderHistory = (orderId: number) => {
  return useQuery<OrderHistoryEntry[], Error>({
    queryKey: [ORDER_HISTORY_QUERY_KEY, orderId],
    queryFn: () => apiClient.orders.getHistory(orderId),
    enabled: !!orderId,
  });
};
